import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, ArrowLeft as PrevIcon,
  BookOpen, Check,
} from "lucide-react";
import { fetchPlan } from "../lib/api";
import { BibleRefLink } from "../lib/bibleRef";
import {
  trackPlanDay, recordPlanDayRead, getPlanProgress,
} from "../lib/readingProgress";
import { Skeleton } from "../components/ui/skeleton";
import ShareButton from "../features/share/ShareButton";

const Section = ({ label, children, index = 0 }) => (
  <section className="mt-10 fade-in" style={{ animationDelay: `${index * 0.06}s` }}>
    <div className="flex items-center gap-3 mb-4">
      <span className="text-[10px] uppercase tracking-[0.3em] text-gold">{label}</span>
      <span className="flex-1 h-px bg-border" />
    </div>
    <div className="text-navy/90 leading-relaxed text-base sm:text-[17px] whitespace-pre-wrap">
      {children}
    </div>
  </section>
);

export default function PlanoDia() {
  const { slug, n } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    fetchPlan(slug).then(setPlan).catch(() => {}).finally(() => setLoading(false));
  }, [slug]);

  // Persist "Continue reading" once both plan and day are resolved.
  useEffect(() => {
    if (!plan) return;
    const dayNum = parseInt(n || "1", 10);
    const day = (plan.days || []).find((d) => d.day === dayNum);
    if (!day) return;
    trackPlanDay({
      slug: plan.slug,
      planTitle: plan.title,
      day: dayNum,
      dayTitle: day.title,
      totalDays: plan.duration_days,
    });
    const prog = getPlanProgress(plan.slug);
    setCompleted((prog?.completed_days || []).includes(dayNum));
  }, [plan, n]);

  const handleMarkRead = () => {
    if (!plan) return;
    const dayNum = parseInt(n || "1", 10);
    recordPlanDayRead({
      slug: plan.slug,
      planTitle: plan.title,
      day: dayNum,
      totalDays: plan.duration_days,
    });
    setCompleted(true);
    toast.success("Dia concluído!", {
      description: "Um passo a mais na sua jornada.",
    });
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto section-padding py-8">
        <Skeleton className="h-6 w-28 mb-4" />
        <Skeleton className="h-10 w-2/3 mb-4" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!plan) return <div className="section-padding py-8"><p className="text-navy/60">Plano não encontrado.</p></div>;

  const dayNum = parseInt(n || "1", 10);
  const day = (plan.days || []).find((d) => d.day === dayNum);
  if (!day) return <div className="section-padding py-8"><p className="text-navy/60">Dia não encontrado.</p></div>;

  const total = plan.duration_days;
  const prevDay = dayNum > 1 ? dayNum - 1 : null;
  const nextDay = dayNum < total ? dayNum + 1 : null;
  const progress = Math.round((dayNum / total) * 100);

  // Optional link to a Bible chapter for reading plans that include one.
  const bibleRef = day.bible_ref && day.bible_ref.book_slug && day.bible_ref.chapter
    ? day.bible_ref
    : null;
  const bibleHref = bibleRef
    ? `/biblia/${bibleRef.book_slug}/${bibleRef.chapter}`
    : null;

  return (
    <div className="max-w-3xl mx-auto section-padding py-8" data-testid="plan-day-page">
      <Link
        to={`/planos/${plan.slug}`}
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy/60 hover:text-navy mb-6"
        data-testid="plan-day-back"
      >
        <ArrowLeft className="w-4 h-4" /> {plan.title}
      </Link>

      {/* Progress */}
      <div className="flex items-center gap-3 mb-8">
        <div className="text-xs text-navy/60">
          Dia <span className="font-medium text-navy">{dayNum}</span> de {total}
        </div>
        <div className="flex-1 h-1 bg-border rounded-full overflow-hidden">
          <div
            className="h-full bg-gold transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <article>
        <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50">
          {plan.title} · dia {dayNum}
        </p>
        <h1 className="font-display text-4xl sm:text-5xl text-navy mt-3 leading-[1.08]" data-testid="plan-day-title">
          {day.title}
        </h1>

        {(day.verse_text || day.verse_ref) && (
          <blockquote
            className="mt-6 border-l-2 border-gold/70 pl-5 py-1 font-display text-xl text-navy/90 leading-snug"
            data-testid="plan-day-verse"
          >
            {day.verse_text && <>“{day.verse_text}”</>}
            {day.verse_ref && (
              <footer className="mt-3 text-sm not-italic font-body tracking-wide">
                <BibleRefLink
                  refText={day.verse_ref}
                  className="text-gold hover:underline hover:text-navy transition-colors"
                  testid="plan-day-verse-ref-link"
                />
              </footer>
            )}
          </blockquote>
        )}

        {/* "Open the Bible" CTA for Bible-reading plans */}
        {bibleHref && (
          <Link
            to={bibleHref}
            data-testid="plan-day-bible-link"
            className="mt-6 inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-navy text-white hover:bg-gold hover:text-navy px-6 py-3 rounded-full text-[11px] uppercase tracking-widest transition-colors"
          >
            <BookOpen className="w-4 h-4" />
            Ler na Bíblia <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        )}

        {day.reflection && <Section label="Reflexão" index={0}>{day.reflection}</Section>}
        {day.application && <Section label="Aplicação prática" index={1}>{day.application}</Section>}
        {day.challenge && (
          <Section label="Desafio do dia" index={2}>
            <p data-testid="plan-day-challenge" className="text-navy/85">
              {day.challenge}
            </p>
          </Section>
        )}
        {day.prayer && (
          <Section label="Oração" index={3}>
            <p className="italic text-navy/85">{day.prayer}</p>
          </Section>
        )}

        <div className="mt-8 flex justify-end" data-testid="plan-day-share-slot">
          <ShareButton
            variant="pill"
            label="Compartilhar Esperança"
            contentType="plan"
            contentId={slug}
            title={day.title}
            mainText={day.application}
            verse={day.verse_text}
            verseRef={day.verse_ref}
            application={day.application}
            prayer={day.prayer}
            reflection={day.reflection}
            theme={plan?.title}
          />
        </div>

        {/* Mark as read */}
        <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-3">
          <button
            type="button"
            onClick={handleMarkRead}
            disabled={completed}
            data-testid="plan-day-mark-read"
            data-completed={completed ? "true" : "false"}
            className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-[11px] uppercase tracking-widest transition-colors ${
              completed
                ? "bg-gold text-navy cursor-default"
                : "bg-navy text-white hover:bg-gold hover:text-navy"
            }`}
          >
            <Check className="w-3.5 h-3.5" />
            {completed ? "Dia concluído" : "Marcar como lido"}
          </button>
          <span className="text-[11px] text-navy/50">
            Progresso salvo neste dispositivo.
          </span>
        </div>
      </article>

      {/* Navigation */}
      <div className="mt-14 flex items-center justify-between gap-3">
        {prevDay ? (
          <Link
            to={`/planos/${plan.slug}/dia/${prevDay}`}
            data-testid="plan-day-prev"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy/60 hover:text-navy"
          >
            <PrevIcon className="w-4 h-4" /> Dia anterior
          </Link>
        ) : <span />}

        {nextDay ? (
          <Link
            to={`/planos/${plan.slug}/dia/${nextDay}`}
            data-testid="plan-day-next"
            className="inline-flex items-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy px-5 py-2.5 rounded-full text-xs uppercase tracking-widest transition-colors"
          >
            Próximo dia <ArrowRight className="w-4 h-4" />
          </Link>
        ) : (
          <Link
            to={`/planos/${plan.slug}`}
            data-testid="plan-day-finish"
            className="inline-flex items-center gap-2 bg-gold text-navy hover:bg-navy hover:text-white px-5 py-2.5 rounded-full text-xs uppercase tracking-widest transition-colors"
          >
            Concluir plano <ArrowRight className="w-4 h-4" />
          </Link>
        )}
      </div>
    </div>
  );
}
